/**
 * Server functions for CIH coaching articles.
 *
 * Coaching articles are L2 coach-specific content linked to a challenge.
 * They are authored by coaches (via Clerk auth) and reviewed by admins.
 *
 * Coach-facing functions use authMiddleware; admin functions need a `cih` permission.
 * Public reader uses no middleware (published + slug lookup).
 *
 * Aliases: coaching articles, cih articles, article crud, article lifecycle
 */

import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { authMiddleware } from "~/lib/auth-middleware";
import { permissionMiddleware } from "~/lib/permission-middleware";
import { ContentStatus } from "~/lib/schemas/db-enums-cih";
import type { JsonValue } from "~/lib/shared/json";

// ============================================================================
// Types
// ============================================================================

export interface ArticleRow {
  id: string;
  slug: string;
  title: string;
  description: string;
  body: JsonValue | null;
  coachId: string | null;
  authorName: string | null;
  authorTitle: string | null;
  challengeId: string;
  status: string;
  authorId: string | null;
  reviewedBy: string | null;
  reviewNote: string | null;
  submittedAt: Date | null;
  publishedAt: Date | null;
  version: number;
  draftOf: string | null;
  createdAt: Date | null;
  updatedAt: Date | null;
}

// ============================================================================
// Query Keys
// ============================================================================

export const articleQueryKeys = {
  mine: ["articles", "mine"] as const,
  adminList: (status?: string) =>
    ["articles", "admin", "list", status ?? "all"] as const,
  detail: (id: string) => ["articles", id] as const,
};

// ============================================================================
// Coach-Facing Server Functions (authMiddleware)
// ============================================================================

/**
 * The `articles` asset gate, for every coach-facing entry point in this file.
 *
 * Articles are a paid collection. A read hands over the body, and a write
 * spends the editorial pipeline on behalf of a coach whose plan does not open
 * it — so the reads and the mutations answer to the same rule. The published
 * article stays public; only the coach's own workspace copy is gated, and the
 * admin functions below are untouched.
 */
async function assertArticlesOpen(coachId: string): Promise<void> {
  const { assertNovaAccessForCoachId } =
    await import("~/lib/server/nova/assets/gate");
  await assertNovaAccessForCoachId({
    coachId,
    thing: { kind: "asset", assetKey: "articles" },
  });
}

/**
 * List all articles authored by the authenticated coach (own articles only).
 */
export const getMyArticlesFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .handler(async ({ context }): Promise<ArticleRow[]> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { coachingArticles, coaches } = await import("~/../drizzle/schema");
    const { eq } = await import("drizzle-orm");

    const db = await getDBWithInit();

    // Resolve the coach record for this user
    const [coach] = await db
      .select({ id: coaches.id })
      .from(coaches)
      .where(eq(coaches.userId, context.user.id));

    if (!coach) return [];

    // Every row carries its body, so this is a content read of the asset.
    await assertArticlesOpen(coach.id);

    return db
      .select()
      .from(coachingArticles)
      .where(eq(coachingArticles.coachId, coach.id))
      .orderBy(coachingArticles.updatedAt);
  });

/**
 * Create a new coaching article as the authenticated coach.
 *
 * Auth + coach-resolution lives here; the actual insert is delegated to
 * `createArticleDraftForCoach` so the Nova `cih-article` skill can reuse
 * the same write path (it already has a verified `coachId`, so it skips
 * the userId → coach lookup the auth path needs).
 */
export const createArticleFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(
    z.object({
      slug: z.string().min(1),
      title: z.string().min(1),
      description: z.string().min(1),
      challengeId: z.string().uuid(),
      body: z.unknown().optional(),
    }),
  )
  .handler(async ({ data, context }): Promise<ArticleRow> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { coaches } = await import("~/../drizzle/schema");
    const { eq } = await import("drizzle-orm");

    const db = await getDBWithInit();

    const [coach] = await db
      .select({ id: coaches.id })
      .from(coaches)
      .where(eq(coaches.userId, context.user.id));

    if (!coach) throw new Error("Coach profile not found for this user");

    await assertArticlesOpen(coach.id);

    const { createArticleDraftForCoach } =
      await import("~/lib/server/cih/articles");

    return createArticleDraftForCoach({
      coachId: coach.id,
      slug: data.slug,
      title: data.title,
      description: data.description,
      challengeId: data.challengeId,
      body: data.body as JsonValue | undefined,
      authorId: context.user.id,
    });
  });

/**
 * Update a coaching article (coach can only edit their own draft articles).
 *
 * A coach may edit:
 *   - a brand-new draft (status=draft, draftOf=null),
 *   - a draft sent back for revision after feedback (status=draft),
 *   - a draft-of-published they created via `createMyArticleDraftFn` to edit
 *     a live article (status=draft, draftOf=<published id>).
 */
export const updateArticleFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(
    z.object({
      id: z.string().uuid(),
      title: z.string().min(1).optional(),
      description: z.string().min(1).optional(),
      body: z.unknown().optional(),
    }),
  )
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { coaches } = await import("~/../drizzle/schema");
    const { eq } = await import("drizzle-orm");
    const { updateCoachArticleDraft } =
      await import("~/lib/server/cih/articles");

    const db = await getDBWithInit();

    const [coach] = await db
      .select({ id: coaches.id })
      .from(coaches)
      .where(eq(coaches.userId, context.user.id));

    if (!coach) throw new Error("Coach profile not found for this user");

    await assertArticlesOpen(coach.id);

    await updateCoachArticleDraft({
      articleId: data.id,
      coachId: coach.id,
      title: data.title,
      description: data.description,
      body: data.body,
    });
    return { success: true };
  });

/**
 * Coach submits their article for admin review.
 */
export const submitArticleForReviewFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { coaches } = await import("~/../drizzle/schema");
    const { eq } = await import("drizzle-orm");

    const db = await getDBWithInit();

    const [coach] = await db
      .select({ id: coaches.id })
      .from(coaches)
      .where(eq(coaches.userId, context.user.id));

    if (!coach) throw new Error("Coach profile not found for this user");

    await assertArticlesOpen(coach.id);

    // Verify ownership before transitioning
    const { coachingArticles } = await import("~/../drizzle/schema");
    const { and } = await import("drizzle-orm");
    const [existing] = await db
      .select({ id: coachingArticles.id })
      .from(coachingArticles)
      .where(
        and(
          eq(coachingArticles.id, data.id),
          eq(coachingArticles.coachId, coach.id),
        ),
      );

    if (!existing) throw new Error("Article not found");

    const lifecycle = await import("~/lib/server/cih/content-lifecycle");
    await lifecycle.submitForReview(coachingArticles, data.id, context.user.id);
    return { success: true };
  });

/**
 * Coach withdraws their own in_review article back to draft so they can keep
 * editing. Useful when they spot something they want to change after
 * submitting but before the editorial team picks it up.
 */
export const withdrawArticleFromReviewFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { coachingArticles, coaches } = await import("~/../drizzle/schema");
    const { and, eq } = await import("drizzle-orm");

    const db = await getDBWithInit();

    const [coach] = await db
      .select({ id: coaches.id })
      .from(coaches)
      .where(eq(coaches.userId, context.user.id));

    if (!coach) throw new Error("Coach profile not found for this user");

    await assertArticlesOpen(coach.id);

    const [existing] = await db
      .select({ id: coachingArticles.id })
      .from(coachingArticles)
      .where(
        and(
          eq(coachingArticles.id, data.id),
          eq(coachingArticles.coachId, coach.id),
        ),
      );

    if (!existing) throw new Error("Article not found");

    const lifecycle = await import("~/lib/server/cih/content-lifecycle");
    await lifecycle.withdrawFromReview(coachingArticles, data.id);
    return { success: true };
  });

/**
 * Coach archives (or discards) one of their own draft articles. Standalone
 * drafts are soft-archived; edits-in-flight of a live article (`draftOf` set)
 * are discarded outright, leaving the live article untouched. Only drafts can
 * be removed this way — in_review must be withdrawn first, published must be
 * edited. The `discarded` flag lets the UI tailor its confirmation copy.
 */
export const archiveMyArticleFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(
    async ({
      data,
      context,
    }): Promise<{ success: boolean; discarded: boolean }> => {
      const { getDBWithInit } = await import("~/lib/db");
      const { coaches } = await import("~/../drizzle/schema");
      const { eq } = await import("drizzle-orm");

      const db = await getDBWithInit();
      const [coach] = await db
        .select({ id: coaches.id })
        .from(coaches)
        .where(eq(coaches.userId, context.user.id));

      if (!coach) throw new Error("Coach profile not found for this user");

      await assertArticlesOpen(coach.id);

      const { archiveOrDiscardCoachDraft } =
        await import("~/lib/server/cih/articles");
      return archiveOrDiscardCoachDraft({
        articleId: data.id,
        coachId: coach.id,
        userId: context.user.id,
      });
    },
  );

// ============================================================================
// Admin-Facing Server Functions (permissionMiddleware)
// ============================================================================

export interface AdminArticleRow extends ArticleRow {
  coachUsername: string | null;
  coachFullName: string | null;
  challengeTitle: string;
  challengeSlug: string | null;
  createdByNova: boolean;
}

/**
 * List articles pending review (admin). Optionally filter by status.
 * Includes coach and challenge display info for richer admin UI.
 */
export const getArticlesForReviewFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:read")])
  .validator(z.object({ status: z.string().optional() }).optional())
  .handler(async ({ data }): Promise<AdminArticleRow[]> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { coachingArticles, coaches, challenges, novaQueueItems } =
      await import("~/../drizzle/schema");
    const { eq, and, like, getTableColumns, sql } = await import("drizzle-orm");

    const db = await getDBWithInit();
    // No longer filters out draft-of rows: in_review edits of published
    // articles are themselves submissions awaiting review.
    const conditions = [];
    if (data?.status) {
      // eslint-disable-next-line typescript-eslint/no-explicit-any -- status is a DB enum; cast required when filtering by string value at runtime
      conditions.push(eq(coachingArticles.status, data.status as any));
    }

    const rows = await db
      .select({
        ...getTableColumns(coachingArticles),
        coachUsername: coaches.username,
        coachFullName: coaches.fullName,
        challengeTitle: challenges.title,
        challengeSlug: challenges.slug,
        novaGeneratedBy: novaQueueItems.generatedBy,
      })
      .from(coachingArticles)
      .leftJoin(coaches, eq(coaches.id, coachingArticles.coachId))
      .innerJoin(challenges, eq(challenges.id, coachingArticles.challengeId))
      .leftJoin(
        novaQueueItems,
        and(
          eq(novaQueueItems.refTable, sql`'coaching_articles'`),
          eq(novaQueueItems.refId, coachingArticles.id),
          like(novaQueueItems.generatedBy, "nova:%"),
        ),
      )
      .where(and(...conditions))
      .orderBy(coachingArticles.submittedAt);

    return rows.map(({ novaGeneratedBy, ...row }) => ({
      ...row,
      createdByNova: novaGeneratedBy != null,
    }));
  });

/**
 * Get a single article by ID (admin). Includes coach and challenge display info.
 */
export const getArticleFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:read")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }): Promise<AdminArticleRow | null> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { coachingArticles, coaches, challenges, novaQueueItems } =
      await import("~/../drizzle/schema");
    const { and, eq, like, getTableColumns, sql } = await import("drizzle-orm");

    const db = await getDBWithInit();
    const [row] = await db
      .select({
        ...getTableColumns(coachingArticles),
        coachUsername: coaches.username,
        coachFullName: coaches.fullName,
        challengeTitle: challenges.title,
        challengeSlug: challenges.slug,
        novaGeneratedBy: novaQueueItems.generatedBy,
      })
      .from(coachingArticles)
      .leftJoin(coaches, eq(coaches.id, coachingArticles.coachId))
      .innerJoin(challenges, eq(challenges.id, coachingArticles.challengeId))
      .leftJoin(
        novaQueueItems,
        and(
          eq(novaQueueItems.refTable, sql`'coaching_articles'`),
          eq(novaQueueItems.refId, coachingArticles.id),
          like(novaQueueItems.generatedBy, "nova:%"),
        ),
      )
      .where(eq(coachingArticles.id, data.id));
    if (!row) return null;
    const { novaGeneratedBy, ...rest } = row;
    return { ...rest, createdByNova: novaGeneratedBy != null };
  });

/**
 * Update an article as an admin. Editorial staff can edit:
 *   - a draft they created via `createArticleDraftFn` from a published article
 *     (before promoting it back to live),
 *   - a coach-less editorial draft they own,
 *   - a coach-authored article currently `in_review` (minor edits: typos,
 *     grammar) — saved edits stay on the in_review row until approved.
 *
 * Published and archived rows are never edited directly: published needs a
 * draft-and-promote cycle, archived needs a restore first.
 */
export const adminUpdateArticleFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(
    z.object({
      id: z.string().uuid(),
      title: z.string().min(1).optional(),
      description: z.string().min(1).optional(),
      body: z.unknown().optional(),
      authorName: z.string().min(1).optional(),
      authorTitle: z.string().optional(),
    }),
  )
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { coachingArticles } = await import("~/../drizzle/schema");
    const { and, eq } = await import("drizzle-orm");

    const db = await getDBWithInit();
    const [existing] = await db
      .select({ status: coachingArticles.status })
      .from(coachingArticles)
      .where(eq(coachingArticles.id, data.id));
    if (!existing) throw new Error("Article not found");
    if (existing.status !== "draft" && existing.status !== "in_review") {
      throw new Error(
        `Cannot edit article: only draft or in-review articles can be modified (current status: '${existing.status}')`,
      );
    }

    const { id, ...fields } = data;
    const updates: Record<string, unknown> = Object.fromEntries(
      Object.entries(fields).filter(([, value]) => value !== undefined),
    );
    if (Object.keys(updates).length === 0) return { success: true };

    await db
      .update(coachingArticles)
      .set(updates)
      .where(and(eq(coachingArticles.id, id)));
    return { success: true };
  });

/**
 * Archive an article (admin). Soft-deletes via status so the row is hidden
 * from active queues but remains for audit / restore.
 *
 * Edits-in-flight against a live article (`draftOf` set) are hard-deleted
 * instead: the partial unique index on `draftOf` only permits one draft per
 * parent, so leaving an archived draft around would block the admin from ever
 * opening a fresh edit on the same live article. Discarding the draft also
 * matches the mental model — "throw away these edits, leave the live article
 * alone".
 */
export const adminArchiveArticleFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(
    z.object({
      id: z.string().uuid(),
      note: z.string().trim().min(1).optional(),
    }),
  )
  .handler(
    async ({
      data,
      context,
    }): Promise<{ success: boolean; discarded: boolean }> => {
      const { getDBWithInit } = await import("~/lib/db");
      const { coachingArticles } = await import("~/../drizzle/schema");
      const { eq } = await import("drizzle-orm");

      const db = await getDBWithInit();
      const [row] = await db
        .select({
          status: coachingArticles.status,
          draftOf: coachingArticles.draftOf,
          coachId: coachingArticles.coachId,
        })
        .from(coachingArticles)
        .where(eq(coachingArticles.id, data.id));
      if (!row) throw new Error("Article not found");

      if (row.draftOf != null) {
        // Discarded edits: the live article is untouched, so the coach has
        // nothing to be notified about. Just drop the row.
        await db
          .delete(coachingArticles)
          .where(eq(coachingArticles.id, data.id));
        return { success: true, discarded: true };
      }

      const lifecycle = await import("~/lib/server/cih/content-lifecycle");
      await lifecycle.archive(coachingArticles, data.id, context.user.id);

      // Persist the archive reason on the row so the audit trail and any
      // future "previous feedback" surface in the UI can show it.
      if (data.note) {
        await db
          .update(coachingArticles)
          .set({ reviewNote: data.note })
          .where(eq(coachingArticles.id, data.id));
      }

      // Coach-less editorial articles have no recipient.
      if (row.coachId) {
        const { notifyCoachArticleArchived } =
          await import("~/lib/server/cih/article-notifications");
        await notifyCoachArticleArchived(data.id, data.note ?? null);
      }

      return { success: true, discarded: false };
    },
  );

/**
 * Restore an archived article to draft (admin). The row re-enters the editing
 * pipeline; the admin (or coach) can then re-submit / re-publish.
 */
export const adminRestoreArticleFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { coachingArticles } = await import("~/../drizzle/schema");
    const lifecycle = await import("~/lib/server/cih/content-lifecycle");
    await lifecycle.restoreFromArchive(
      coachingArticles,
      data.id,
      context.user.id,
    );
    return { success: true };
  });

/**
 * Approve an article in review — transitions it to published (admin).
 *
 * When the article being approved is a coach-authored edit of an already-
 * published article (`draftOf` is set), the lifecycle helper promotes the
 * draft into its parent instead of publishing as a separate row. Either
 * way, the coach is notified that their article is live.
 */
export const approveArticleFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { coachingArticles } = await import("~/../drizzle/schema");
    const lifecycle = await import("~/lib/server/cih/content-lifecycle");
    const { notifyCoachArticlePublished } =
      await import("~/lib/server/cih/article-notifications");

    // Resolve metadata before mutation (the row id may change on promote).
    const { getDBWithInit } = await import("~/lib/db");
    const { eq } = await import("drizzle-orm");
    const db = await getDBWithInit();
    const [pre] = await db
      .select({
        coachId: coachingArticles.coachId,
        draftOf: coachingArticles.draftOf,
      })
      .from(coachingArticles)
      .where(eq(coachingArticles.id, data.id));

    if (!pre) throw new Error("Article not found");

    await lifecycle.approve(coachingArticles, data.id, context.user.id);

    // After approve+promote the draft row is gone; resolve the live id.
    const liveId = pre.draftOf ?? data.id;
    // Coach-less editorial articles have no coach to notify.
    if (pre.coachId) {
      await notifyCoachArticlePublished(liveId, pre.coachId);
    }
    return { success: true };
  });

/**
 * Return an article to the coach for revision (admin). Notifies the coach
 * that feedback is waiting.
 */
export const returnArticleToDraftFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(
    z.object({
      id: z.string().uuid(),
      note: z.string().min(1),
    }),
  )
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { coachingArticles } = await import("~/../drizzle/schema");
    const lifecycle = await import("~/lib/server/cih/content-lifecycle");
    const { notifyCoachArticleReturned } =
      await import("~/lib/server/cih/article-notifications");
    await lifecycle.returnToDraft(
      coachingArticles,
      data.id,
      context.user.id,
      data.note,
    );
    await notifyCoachArticleReturned(data.id, data.note);
    return { success: true };
  });

/**
 * Create an editable draft of a published article (admin). Lets editorial
 * staff revise live articles without affecting the live row until they
 * promote the draft.
 */
export const createArticleDraftFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ publishedId: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<ArticleRow> => {
    const { coachingArticles } = await import("~/../drizzle/schema");
    const lifecycle = await import("~/lib/server/cih/content-lifecycle");
    return lifecycle.createDraftOfPublished(
      coachingArticles,
      data.publishedId,
      context.user.id,
    ) as unknown as Promise<ArticleRow>;
  });

/**
 * Promote an admin-edited draft back onto its published parent (admin).
 * No editorial review required — admins are the editorial team.
 */
export const promoteArticleDraftFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ draftId: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<ArticleRow> => {
    const { coachingArticles } = await import("~/../drizzle/schema");
    const lifecycle = await import("~/lib/server/cih/content-lifecycle");
    return lifecycle.promoteDraft(
      coachingArticles,
      data.draftId,
      [],
      context.user.id,
    ) as unknown as Promise<ArticleRow>;
  });

/**
 * Create an admin-authored article with a free-text author (no coach link).
 *
 * Auth + input parsing live here; the insert is delegated to
 * `createAdminArticleDraft`. The new row is a `draft`; publish it directly via
 * `adminPublishArticleFn` (there is no coach to submit it for review).
 */
export const adminCreateArticleFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(
    z.object({
      slug: z.string().min(1),
      title: z.string().min(1),
      description: z.string().min(1),
      challengeId: z.string().uuid(),
      authorName: z.string().min(1),
      authorTitle: z.string().optional(),
      body: z.unknown().optional(),
    }),
  )
  .handler(async ({ data, context }): Promise<ArticleRow> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { challenges } = await import("~/../drizzle/schema");
    const { eq } = await import("drizzle-orm");

    const db = await getDBWithInit();
    const [challenge] = await db
      .select({ id: challenges.id })
      .from(challenges)
      .where(eq(challenges.id, data.challengeId));
    if (!challenge) throw new Error("Challenge not found");

    const { createAdminArticleDraft } =
      await import("~/lib/server/cih/articles");

    return createAdminArticleDraft({
      slug: data.slug,
      title: data.title,
      description: data.description,
      challengeId: data.challengeId,
      authorName: data.authorName,
      authorTitle: data.authorTitle,
      body: data.body as JsonValue | undefined,
      authorId: context.user.id,
    });
  });

/**
 * Admin self-publish: transition a draft directly to published without review.
 * Used for coach-less editorial articles (coach articles go through the
 * submit → approve flow instead).
 */
export const adminPublishArticleFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { coachingArticles } = await import("~/../drizzle/schema");
    const lifecycle = await import("~/lib/server/cih/content-lifecycle");
    await lifecycle.publish(coachingArticles, data.id, context.user.id);
    return { success: true };
  });

/**
 * Coach creates an editable draft of one of their own published articles so
 * they can revise it. The draft must be submitted for editorial review;
 * approving it promotes the edits onto the live article.
 */
export const createMyArticleDraftFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(z.object({ publishedId: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<ArticleRow> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { coachingArticles, coaches } = await import("~/../drizzle/schema");
    const { and, eq } = await import("drizzle-orm");

    const db = await getDBWithInit();
    const [coach] = await db
      .select({ id: coaches.id })
      .from(coaches)
      .where(eq(coaches.userId, context.user.id));

    if (!coach) throw new Error("Coach profile not found for this user");

    await assertArticlesOpen(coach.id);

    // Verify the article belongs to this coach before letting them branch a draft.
    const [owned] = await db
      .select({ id: coachingArticles.id })
      .from(coachingArticles)
      .where(
        and(
          eq(coachingArticles.id, data.publishedId),
          eq(coachingArticles.coachId, coach.id),
        ),
      );
    if (!owned) throw new Error("Article not found");

    const lifecycle = await import("~/lib/server/cih/content-lifecycle");
    return lifecycle.createDraftOfPublished(
      coachingArticles,
      data.publishedId,
      context.user.id,
    ) as unknown as Promise<ArticleRow>;
  });

// ============================================================================
// Public Server Functions (no middleware)
// ============================================================================

export type {
  ArticleCoachCco,
  ArticleRelatedChallenge,
} from "~/lib/server/cih/articles";

/**
 * Get a published coaching article by global slug (public). Returns the
 * article with its coach, challenge, credentials and related challenges so
 * the reader page renders without extra queries.
 */
export const getPublishedArticleBySlugFn = createServerFn({ method: "GET" })
  .validator(z.object({ slug: z.string().trim().min(1) }))
  .handler(async ({ data }) => {
    const { getPublishedArticleBySlug } =
      await import("~/lib/server/cih/articles");
    return getPublishedArticleBySlug(data.slug);
  });

/**
 * List published articles for a given challenge (public). Returns a compact
 * row shape suitable for index lists — includes coach byline fields for
 * rendering attribution on each row.
 */
export const getChallengeArticlesFn = createServerFn({ method: "GET" })
  .validator(z.object({ challengeId: z.string().uuid() }))
  .handler(async ({ data }) => {
    const { getDBWithInit } = await import("~/lib/db");
    const { coachingArticles, coaches } = await import("~/../drizzle/schema");
    const { and, eq, isNull, sql } = await import("drizzle-orm");

    const db = await getDBWithInit();

    return db
      .select({
        id: coachingArticles.id,
        slug: coachingArticles.slug,
        title: coachingArticles.title,
        description: coachingArticles.description,
        publishedAt: coachingArticles.publishedAt,
        coachUsername: coaches.username,
        coachFullName: coaches.fullName,
        coachProfilePictureUrl: coaches.profilePictureUrl,
        authorName: coachingArticles.authorName,
        authorTitle: coachingArticles.authorTitle,
      })
      .from(coachingArticles)
      .leftJoin(coaches, eq(coaches.id, coachingArticles.coachId))
      .where(
        and(
          eq(coachingArticles.challengeId, data.challengeId),
          eq(coachingArticles.status, ContentStatus.Published),
          isNull(coachingArticles.draftOf),
        ),
      )
      .orderBy(sql`${coachingArticles.publishedAt} desc nulls last`);
  });

export type ChallengeArticleRow = Awaited<
  ReturnType<typeof getChallengeArticlesFn>
>[number];

/**
 * List all published coaching articles for a challenge by slug (public).
 * Joins coach + challenge so the sub-list page has attribution without extra
 * queries.
 */
export const getArticlesByChallengeFn = createServerFn({ method: "GET" })
  .validator(z.object({ challengeSlug: z.string() }))
  .handler(async ({ data }) => {
    const { getDBWithInit } = await import("~/lib/db");
    const db = await getDBWithInit();
    const { coachingArticles, coaches, challenges } =
      await import("~/../drizzle/schema");
    const { and, eq, isNull, sql } = await import("drizzle-orm");

    return db
      .select({
        article: coachingArticles,
        coach: {
          username: coaches.username,
          fullName: coaches.fullName,
          profilePictureUrl: coaches.profilePictureUrl,
        },
        challenge: { title: challenges.title, slug: challenges.slug },
      })
      .from(coachingArticles)
      .innerJoin(challenges, eq(challenges.id, coachingArticles.challengeId))
      .leftJoin(coaches, eq(coaches.id, coachingArticles.coachId))
      .where(
        and(
          eq(challenges.slug, data.challengeSlug),
          eq(coachingArticles.status, ContentStatus.Published),
          isNull(coachingArticles.draftOf),
        ),
      )
      .orderBy(sql`${coachingArticles.publishedAt} desc nulls last`);
  });

/**
 * List all published coaching articles (public). Joins coach + challenge so
 * the public index page can render title, byline, and challenge pill in one
 * query. Also attaches each author's CCO credentials (via a batched
 * follow-up query) so the card can show "By Jane Doe · ICF".
 *
 * Pass `limit` to cap the query in SQL (newest first) for bounded surfaces
 * like the homepage; omit it for the full index.
 */
export const getArticlesIndexFn = createServerFn({ method: "GET" })
  .validator(z.object({ limit: z.number().int().positive() }).optional())
  .handler(async ({ data }) => {
    const { listPublishedArticlesForIndex } =
      await import("~/lib/server/cih/articles");
    return listPublishedArticlesForIndex(data?.limit);
  });

export interface MoreArticleRow {
  id: string;
  slug: string;
  title: string;
  description: string;
  publishedAt: Date | null;
  coachUsername: string | null;
  coachFullName: string | null;
  coachProfilePictureUrl: string | null;
  authorName: string | null;
  authorTitle: string | null;
  challengeTitle: string;
  challengeSlug: string | null;
}

/**
 * Get other published articles by the same coach (for article detail "more" section).
 * Excludes the article being read.
 */
export const getMoreArticlesByCoachFn = createServerFn({ method: "GET" })
  .validator(
    z.object({
      coachId: z.string().uuid(),
      excludeArticleId: z.string().uuid(),
      limit: z.number().int().positive().optional(),
    }),
  )
  .handler(async ({ data }): Promise<MoreArticleRow[]> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { coachingArticles, coaches, challenges } =
      await import("~/../drizzle/schema");
    const { and, eq, isNull, ne, sql } = await import("drizzle-orm");

    const db = await getDBWithInit();
    const limit = data.limit ?? 3;

    return db
      .select({
        id: coachingArticles.id,
        slug: coachingArticles.slug,
        title: coachingArticles.title,
        description: coachingArticles.description,
        publishedAt: coachingArticles.publishedAt,
        coachUsername: coaches.username,
        coachFullName: coaches.fullName,
        coachProfilePictureUrl: coaches.profilePictureUrl,
        authorName: coachingArticles.authorName,
        authorTitle: coachingArticles.authorTitle,
        challengeTitle: challenges.title,
        challengeSlug: challenges.slug,
      })
      .from(coachingArticles)
      .innerJoin(coaches, eq(coaches.id, coachingArticles.coachId))
      .innerJoin(challenges, eq(challenges.id, coachingArticles.challengeId))
      .where(
        and(
          eq(coachingArticles.coachId, data.coachId),
          ne(coachingArticles.id, data.excludeArticleId),
          eq(coachingArticles.status, ContentStatus.Published),
          isNull(coachingArticles.draftOf),
        ),
      )
      .orderBy(sql`${coachingArticles.publishedAt} desc nulls last`)
      .limit(limit);
  });

/**
 * Get other published articles on the same challenge (for article detail "more" section).
 * Excludes the article being read.
 */
export const getMoreArticlesByChallengeFn = createServerFn({ method: "GET" })
  .validator(
    z.object({
      challengeId: z.string().uuid(),
      excludeArticleId: z.string().uuid(),
      limit: z.number().int().positive().optional(),
    }),
  )
  .handler(async ({ data }): Promise<MoreArticleRow[]> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { coachingArticles, coaches, challenges } =
      await import("~/../drizzle/schema");
    const { and, eq, isNull, ne, sql } = await import("drizzle-orm");

    const db = await getDBWithInit();
    const limit = data.limit ?? 3;

    return db
      .select({
        id: coachingArticles.id,
        slug: coachingArticles.slug,
        title: coachingArticles.title,
        description: coachingArticles.description,
        publishedAt: coachingArticles.publishedAt,
        coachUsername: coaches.username,
        coachFullName: coaches.fullName,
        coachProfilePictureUrl: coaches.profilePictureUrl,
        authorName: coachingArticles.authorName,
        authorTitle: coachingArticles.authorTitle,
        challengeTitle: challenges.title,
        challengeSlug: challenges.slug,
      })
      .from(coachingArticles)
      .leftJoin(coaches, eq(coaches.id, coachingArticles.coachId))
      .innerJoin(challenges, eq(challenges.id, coachingArticles.challengeId))
      .where(
        and(
          eq(coachingArticles.challengeId, data.challengeId),
          ne(coachingArticles.id, data.excludeArticleId),
          eq(coachingArticles.status, ContentStatus.Published),
          isNull(coachingArticles.draftOf),
        ),
      )
      .orderBy(sql`${coachingArticles.publishedAt} desc nulls last`)
      .limit(limit);
  });

// ============================================================================
// Query Options
// ============================================================================

/**
 * Get a single article owned by the authenticated coach (for editing).
 *
 * Returns `null` when the article doesn't exist or isn't owned by the coach —
 * a genuinely-expected miss (stale link, deleted draft) that the route loader
 * turns into a `notFound()`. We deliberately do NOT throw here so the global
 * Sentry function middleware doesn't page on an ordinary missing row.
 */
export const getMyArticleFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<ArticleRow | null> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { coachingArticles, coaches } = await import("~/../drizzle/schema");
    const { eq, and } = await import("drizzle-orm");

    const db = await getDBWithInit();
    const [coach] = await db
      .select({ id: coaches.id })
      .from(coaches)
      .where(eq(coaches.userId, context.user.id));

    if (!coach) return null;

    // The article body, in the coach's own editor.
    await assertArticlesOpen(coach.id);

    const [row] = await db
      .select()
      .from(coachingArticles)
      .where(
        and(
          eq(coachingArticles.id, data.id),
          eq(coachingArticles.coachId, coach.id),
        ),
      );

    return row ?? null;
  });

export const myArticlesQueryOptions = () =>
  queryOptions({
    queryKey: articleQueryKeys.mine,
    queryFn: () => getMyArticlesFn(),
  });

export const myArticleQueryOptions = (id: string) =>
  queryOptions({
    queryKey: [...articleQueryKeys.mine, id],
    queryFn: () => getMyArticleFn({ data: { id } }),
  });

export const articlesForReviewQueryOptions = (status?: string) =>
  queryOptions({
    queryKey: articleQueryKeys.adminList(status),
    queryFn: () => getArticlesForReviewFn({ data: { status } }),
  });

export const articleQueryOptions = (id: string) =>
  queryOptions({
    queryKey: articleQueryKeys.detail(id),
    queryFn: () => getArticleFn({ data: { id } }),
  });
